// Reset-password links carry `email` and `token` in the query string, and the
// page sends both to the API as query parameters, so they show up in page
// URLs, navigation and XHR breadcrumbs, transactions and replays.

export const REDACTED = "[REDACTED]";

const SENSITIVE_PARTS = ["token", "email", "password", "secret", "signature"];
// Google API keys and S3 object keys travel in URLs as a bare `key`.
const SENSITIVE_URL_PARAMS = new Set(["key"]);

// Only walk this deep; anything deeper is passed through unchanged.
const MAX_DEPTH = 12;

// Email addresses, also percent-encoded, anywhere in free text such as error
// messages and console breadcrumbs.
const EMAIL_PATTERN = /[a-z0-9._%+-]+(?:@|%40)[a-z0-9.-]+\.[a-z]{2,}/gi;

// Recording event types that carry URLs: meta (4) and custom (5), which holds
// breadcrumbs and performance spans. DOM snapshots are masked already.
const URL_RECORDING_EVENT_TYPES = new Set([4, 5]);

function normalize(name: string): string {
  let decoded = name;
  try {
    decoded = decodeURIComponent(name.replace(/\+/g, " "));
  } catch {
    // Keep the raw name.
  }
  return decoded.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isSensitiveField(name: string): boolean {
  const normalized = normalize(name);
  return SENSITIVE_PARTS.some((part) => normalized.includes(part));
}

function isSensitiveUrlParam(name: string): boolean {
  return SENSITIVE_URL_PARAMS.has(normalize(name)) || isSensitiveField(name);
}

/** Redacts sensitive `name=value` pairs in a URL's query and fragment. */
export function scrubUrl(url: string): string {
  if (!url.includes("=")) return url;
  return url.replace(/([?&#])([^=&#?]+)=([^&#]*)/g, (match, separator, name) =>
    isSensitiveUrlParam(name) ? `${separator}${name}=${REDACTED}` : match
  );
}

function redactEmails(text: string): string {
  return text.replace(EMAIL_PATTERN, REDACTED);
}

function scrubDeep(value: unknown, depth: number): unknown {
  if (typeof value === "string") return redactEmails(scrubUrl(value));
  if (depth > MAX_DEPTH || value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubDeep(item, depth + 1));
  }
  if (Object.getPrototypeOf(value) !== Object.prototype) return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isSensitiveField(key) && typeof item === "string"
        ? REDACTED
        : scrubDeep(item, depth + 1),
    ])
  );
}

/**
 * `beforeSend` / `beforeSendTransaction` / `beforeBreadcrumb` hook and event
 * processor: redacts sensitive URL parameters, emails, and string values
 * stored under sensitive keys, anywhere in the object. The input is not mutated.
 */
export function scrubSentryData<T>(data: T): T {
  return scrubDeep(data, 0) as T;
}

/** `beforeAddRecordingEvent` hook for the replay integration. */
export function scrubRecordingEvent<T extends { type: number }>(event: T): T {
  return URL_RECORDING_EVENT_TYPES.has(event.type)
    ? scrubSentryData(event)
    : event;
}
